import { useState } from 'react';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { uploadFile, type UploadedAttachment, type UploadProgress } from '@/lib/uploader';
import { tempId } from '@/lib/tempId';
import i18n from '@/i18n';

/** Max attachments per message. */
export const MAX_COMPOSER_FILES = 10;

/**
 * One file the user has picked but not yet sent. The `completed` attachment is
 * what we POST to the message endpoint once the user hits Send. `controller`
 * lives so the inline ✕ on a pending chip can abort the still-in-flight PUT.
 */
export interface UploadSlot {
  fileId: string;
  name: string;
  size: number;
  /** Source MIME type (file.type) — lets the composer show an image thumbnail
   *  rather than a generic file chip for picture/video attachments. */
  mime: string;
  /** Object URL for an inline thumbnail (images only); revoked on remove. */
  previewUrl?: string;
  /** User chose "send as file" — image goes as a download chip, not inline. */
  asFile?: boolean;
  status: UploadProgress['status'];
  loaded: number;
  error?: string;
  completed?: UploadedAttachment;
  controller: AbortController;
}

// ── Pure slot reducers (tested in isolation) ────────────────────────────────

/** Would adding `adding` files exceed the per-message cap? */
export function withinFileLimit(current: number, adding: number, max = MAX_COMPOSER_FILES): boolean {
  return current + adding <= max;
}

/** Return a new list with the matching slot shallow-merged with `patch`. */
export function patchSlot(slots: UploadSlot[], fileId: string, patch: Partial<UploadSlot>): UploadSlot[] {
  return slots.map((slot) => (slot.fileId === fileId ? { ...slot, ...patch } : slot));
}

/** Return a new list without the matching slot. */
export function removeSlot(slots: UploadSlot[], fileId: string): UploadSlot[] {
  return slots.filter((slot) => slot.fileId !== fileId);
}

/** Prepend `restored` slots, skipping any whose fileId is already present. */
export function restoreSlots(current: UploadSlot[], restored: UploadSlot[]): UploadSlot[] {
  const present = new Set(current.map((s) => s.fileId));
  return [...restored.filter((s) => !present.has(s.fileId)), ...current];
}

/** Revoke the thumbnails of slots that will never be shown again. */
export function releaseSlots(slots: UploadSlot[]): void {
  for (const s of slots) if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
}

/**
 * Composer attachment-upload manager, extracted from Composer. Owns the
 * pending-upload slots and the upload lifecycle (start → progress → done /
 * error / abort). Behaviour-identical to the inline version; the pure slot
 * reducers above are unit-tested and the hook's start/cancel are covered by a
 * renderHook test.
 */
export function useComposerUploads() {
  const [pendingUploads, setPendingUploads] = useState<UploadSlot[]>([]);

  function startUploads(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    if (!withinFileLimit(pendingUploads.length, list.length)) {
      toast.error(i18n.t('composer.tooManyFiles', { count: MAX_COMPOSER_FILES }));
      return;
    }
    for (const file of list) {
      const fileId = tempId('up');
      const controller = new AbortController();
      const previewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined;
      setPendingUploads((prev) => [
        ...prev,
        {
          fileId,
          name: file.name,
          size: file.size,
          mime: file.type,
          previewUrl,
          status: 'preparing',
          loaded: 0,
          controller,
        },
      ]);
      void uploadFile(file, {
        fileId,
        signal: controller.signal,
        onProgress: (p) =>
          setPendingUploads((prev) =>
            patchSlot(prev, fileId, { status: p.status, loaded: p.loaded, error: p.error }),
          ),
      })
        .then((completed) =>
          setPendingUploads((prev) =>
            patchSlot(prev, fileId, { status: 'done', loaded: completed.size, completed }),
          ),
        )
        .catch((err) => {
          if ((err as Error).name === 'AbortError') return;
          toast.error(...errToToast(err, i18n.t('composer.uploadFailed', { name: file.name })));
          setPendingUploads((prev) =>
            patchSlot(prev, fileId, { status: 'error', error: (err as Error).message }),
          );
        });
    }
  }

  /** Toggle the "send as file" flag on an image slot. */
  function toggleAsFile(fileId: string) {
    setPendingUploads((prev) =>
      prev.map((s) => (s.fileId === fileId ? { ...s, asFile: !s.asFile } : s)),
    );
  }

  /** Set "send as file" on every image slot at once (the global toggle). */
  function setAllAsFile(value: boolean) {
    setPendingUploads((prev) =>
      prev.map((s) => (s.previewUrl ? { ...s, asFile: value } : s)),
    );
  }

  function cancelUpload(fileId: string) {
    setPendingUploads((prev) => {
      const slot = prev.find((u) => u.fileId === fileId);
      slot?.controller.abort();
      if (slot?.previewUrl) URL.revokeObjectURL(slot.previewUrl);
      return removeSlot(prev, fileId);
    });
  }

  /** Drop all slots (called after a successful send). Does not abort — the
   *  uploads already resolved by send time. */
  function clear() {
    setPendingUploads((prev) => {
      for (const s of prev) if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
      return [];
    });
  }

  /** Empty the tray at send time WITHOUT revoking thumbnails: the slots may
   *  come back via `restore` if the send fails. Call `release` once they
   *  are no longer needed. */
  function detach() {
    setPendingUploads([]);
  }

  /** Put slots of a failed send back in front of anything added since. */
  function restore(slots: UploadSlot[]) {
    if (slots.length === 0) return;
    setPendingUploads((prev) => restoreSlots(prev, slots));
  }

  return { pendingUploads, startUploads, cancelUpload, toggleAsFile, setAllAsFile, clear, detach, restore };
}
